import React, { useState } from 'react';
import { Plus, Trash2, Check, Database, X, Sliders, ArrowLeft, ArrowRight } from 'lucide-react';

const PricingPackagesSection = ({ packages, setPackages, api, showStatus }) => {
  const [editingLimitIdx, setEditingLimitIdx] = useState(null);

  const movePackage = (fromIdx, toIdx) => {
    if (!packages || toIdx < 0 || toIdx >= packages.length) return;
    const updated = [...packages];
    const [movedItem] = updated.splice(fromIdx, 1);
    updated.splice(toIdx, 0, movedItem);
    setPackages(updated);
  };

  return (
    <div className="sa-section fade-in">
      <div className="sa-section-header">
        <div>
          <h2>Manage Pricing Packages</h2>
          <p className="subtitle">Edit package details, resource limits, and display positions.</p>
        </div>
        <button className="sa-btn sa-btn-primary" onClick={() => {
          setPackages([...(packages || []), { tierId: `custom_${Date.now()}`, tier: 'New Package', price: '0', desc: 'Description', features: ['Feature 1'], disabledFeatures: ['Feature 2'] }]);
        }}>
          <Plus size={14} strokeWidth={3} /> Add Package
        </button>
      </div>

      <div style={{ padding: '1rem 0 4rem', display: 'flex', gap: '1.5rem', flexWrap: 'wrap', alignItems: 'stretch' }}>
        {packages && packages.map((pkg, idx) => (
          <div key={idx} className="sa-card" style={{
            padding: '1.75rem 1.25rem 1.5rem',
            width: '320px',
            minWidth: '280px',
            flex: '1 1 300px',
            maxWidth: '350px',
            position: 'relative',
            background: 'var(--surface-2)',
            border: (pkg.tier === 'Pro Agency' || pkg.tierId === 'pro') ? '2px solid var(--primary)' : ((pkg.tier === 'Enterprise' || pkg.tierId === 'enterprise') ? '1px solid var(--accent)' : '1px solid var(--border)'),
            borderRadius: '16px',
            display: 'flex',
            flexDirection: 'column',
            justify: 'space-between'
          }}>
            {(pkg.tier === 'Pro Agency' || pkg.tierId === 'pro') && (
              <div style={{ position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)', background: 'var(--primary)', color: 'white', padding: '0.2rem 1rem', borderRadius: '16px', fontSize: '0.65rem', fontWeight: 800, letterSpacing: '1px', boxShadow: '0 4px 12px rgba(var(--primary-rgb), 0.3)' }}>
                MOST POPULAR
              </div>
            )}
            {(pkg.tier === 'Enterprise' || pkg.tierId === 'enterprise') && (
              <div style={{ position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)', background: 'var(--accent)', color: 'white', padding: '0.2rem 1rem', borderRadius: '16px', fontSize: '0.65rem', fontWeight: 800, letterSpacing: '1px', boxShadow: '0 4px 12px rgba(var(--accent-rgb, 245, 166, 35), 0.3)' }}>
                FOR TEAMS
              </div>
            )}

            {/* Header Actions: Position controls & Trash */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                <button
                  title="Move Left"
                  disabled={idx === 0}
                  style={{
                    background: 'var(--surface-3)',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    width: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: idx === 0 ? 'not-allowed' : 'pointer',
                    color: 'var(--text-main)',
                    opacity: idx === 0 ? 0.3 : 1,
                    transition: 'all 0.2s'
                  }}
                  onClick={() => movePackage(idx, idx - 1)}
                >
                  <ArrowLeft size={16} />
                </button>
                <button
                  title="Move Right"
                  disabled={idx === packages.length - 1}
                  style={{
                    background: 'var(--surface-3)',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    width: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: idx === packages.length - 1 ? 'not-allowed' : 'pointer',
                    color: 'var(--text-main)',
                    opacity: idx === packages.length - 1 ? 0.3 : 1,
                    transition: 'all 0.2s'
                  }}
                  onClick={() => movePackage(idx, idx + 1)}
                >
                  <ArrowRight size={16} />
                </button>
              </div>

              <button
                title="Delete Package"
                style={{
                  background: 'rgba(239, 68, 68, 0.12)',
                  border: '1px solid rgba(239, 68, 68, 0.25)',
                  borderRadius: '8px',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: 'var(--danger, #ef4444)',
                  transition: 'all 0.2s'
                }}
                onClick={() => {
                  const newPackages = packages.filter((_, i) => i !== idx);
                  setPackages(newPackages);
                }}
              >
                <Trash2 size={16} />
              </button>
            </div>

            {/* Inline Editable Fields */}
            <div style={{ textAlign: 'center', marginBottom: '1.25rem' }}>
              <input
                type="text"
                style={{
                  width: '100%',
                  textAlign: 'center',
                  background: 'transparent',
                  border: '1px dashed var(--border)',
                  borderRadius: '6px',
                  color: 'var(--text-main)',
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  padding: '0.25rem',
                  marginBottom: '0.5rem',
                  outline: 'none'
                }}
                value={pkg.tier || ''}
                onChange={(e) => {
                  const newPackages = [...packages];
                  newPackages[idx].tier = e.target.value;
                  setPackages(newPackages);
                }}
                placeholder="Package Name"
              />

              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '3.5rem' }}>
                <span style={{ fontSize: '0.9rem', marginRight: '0.2rem', color: 'var(--text-main)', fontWeight: 600 }}>$</span>
                <input
                  type="text"
                  style={{
                    width: '120px',
                    textAlign: 'center',
                    background: 'transparent',
                    border: '1px dashed var(--border)',
                    borderRadius: '6px',
                    color: 'var(--text-main)',
                    fontSize: '2rem',
                    fontWeight: 800,
                    outline: 'none'
                  }}
                  value={pkg.price ?? ''}
                  onChange={(e) => {
                    const newPackages = [...packages];
                    newPackages[idx].price = e.target.value;
                    setPackages(newPackages);
                  }}
                  placeholder="Price or Custom"
                />
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginLeft: '0.2rem', fontWeight: 500 }}>/mo</span>
              </div>
            </div>

            <textarea
              style={{
                width: '100%',
                background: 'transparent',
                border: '1px dashed var(--border)',
                borderRadius: '6px',
                textAlign: 'center',
                fontSize: '0.75rem',
                color: 'var(--text-muted)',
                marginBottom: '1.5rem',
                lineHeight: 1.4,
                minHeight: '45px',
                padding: '0.4rem',
                resize: 'none',
                outline: 'none'
              }}
              value={pkg.desc || ''}
              onChange={(e) => {
                const newPackages = [...packages];
                newPackages[idx].desc = e.target.value;
                setPackages(newPackages);
              }}
              placeholder="Package description..."
            />

            <div style={{ marginBottom: '1.25rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-main)', marginBottom: '0.5rem' }}>
                <Check size={14} strokeWidth={2.5} color="var(--success, #10b981)" /> Included (one per line)
              </div>
              <textarea
                style={{
                  width: '100%', border: 'none', borderLeft: '2px solid var(--primary)',
                  background: 'var(--surface-3)', padding: '0.4rem 0.5rem', fontSize: '0.75rem', color: 'var(--text-main)',
                  minHeight: '70px', resize: 'vertical', outline: 'none', fontFamily: 'inherit', lineHeight: 1.5, borderRadius: '0 6px 6px 0'
                }}
                value={Array.isArray(pkg.features) ? pkg.features.join('\n') : (pkg.features || '')}
                onChange={(e) => {
                  const newPackages = [...packages];
                  newPackages[idx].features = e.target.value.split('\n');
                  setPackages(newPackages);
                }}
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                <Check size={14} strokeWidth={2.5} color="var(--text-tertiary)" /> Disabled (one per line)
              </div>
              <textarea
                style={{
                  width: '100%', border: 'none', borderLeft: '2px solid var(--border)',
                  background: 'var(--surface-3)', padding: '0.4rem 0.5rem', fontSize: '0.75rem', color: 'var(--text-muted)',
                  minHeight: '40px', resize: 'vertical', outline: 'none', fontFamily: 'inherit', lineHeight: 1.5, borderRadius: '0 6px 6px 0'
                }}
                value={Array.isArray(pkg.disabledFeatures) ? pkg.disabledFeatures.join('\n') : (pkg.disabledFeatures || '')}
                onChange={(e) => {
                  const newPackages = [...packages];
                  newPackages[idx].disabledFeatures = e.target.value.split('\n');
                  setPackages(newPackages);
                }}
              />
            </div>

            <div style={{ borderTop: '1px solid var(--border)', paddingTop: '1rem', display: 'flex', justifyContent: 'center' }}>
              <button style={{ background: 'transparent', border: 'none', color: 'var(--primary)', fontWeight: 600, fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }} onClick={() => {
                setEditingLimitIdx(idx);
              }}>
                <Database size={16} strokeWidth={2} /> Configure Limits
              </button>
            </div>
          </div>
        ))}
      </div>

      <div style={{ position: 'fixed', bottom: '2rem', right: '3rem', zIndex: 100 }}>
        <button className="sa-btn sa-btn-primary" style={{ padding: '0.75rem 1.75rem', fontWeight: 600, fontSize: '0.85rem', boxShadow: '0 8px 20px rgba(0,0,0,0.2)' }} onClick={async () => {
          try {
            if (typeof packages !== 'undefined' && packages) {
              await api.updatePackages(packages);
              showStatus('Packages updated successfully', 'success');
            }
          } catch (err) {
            showStatus('Error saving packages', 'error');
          }
        }}>Save Changes</button>
      </div>

      {editingLimitIdx !== null && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'transparent', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="sa-card fade-in" style={{ width: '600px', maxHeight: '90vh', overflowY: 'auto', background: 'var(--bg-main, #ffffff)', padding: '0', borderRadius: '16px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>

            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.5rem 2rem', borderBottom: '1px solid var(--border-subtle, #f1f5f9)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
                <div style={{ background: 'var(--primary-light, #eff6ff)', color: 'var(--primary, #3b82f6)', padding: '0.5rem', borderRadius: '8px', display: 'flex' }}>
                  <Sliders size={20} strokeWidth={2.5} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-main, #0f172a)', letterSpacing: '-0.02em' }}>Configure Limits</h3>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-muted, #64748b)', marginTop: '0.15rem', fontWeight: 500 }}>{packages[editingLimitIdx]?.tier} Package</div>
                </div>
              </div>
              <button
                style={{ background: 'var(--bg-subtle, #f8fafc)', border: 'none', cursor: 'pointer', color: 'var(--text-muted, #64748b)', padding: '0.5rem', borderRadius: '50%', display: 'flex', transition: 'all 0.2s', outline: 'none' }}
                onClick={() => setEditingLimitIdx(null)}
                onMouseOver={(e) => { e.currentTarget.style.background = '#e2e8f0'; e.currentTarget.style.color = '#0f172a'; }}
                onMouseOut={(e) => { e.currentTarget.style.background = 'var(--bg-subtle, #f8fafc)'; e.currentTarget.style.color = 'var(--text-muted, #64748b)'; }}
              >
                <X size={18} strokeWidth={2.5} />
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '2rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted, #64748b)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.5rem' }}>Max Deployments</label>
                <input
                  type="number"
                  style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border, #cbd5e1)', borderRadius: '8px', fontSize: '0.95rem', background: '#f8fafc', color: 'var(--text-main, #0f172a)', transition: 'all 0.2s', outline: 'none', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)' }}
                  onFocus={(e) => { e.target.style.borderColor = 'var(--primary, #3b82f6)'; e.target.style.background = '#fff'; e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)'; }}
                  onBlur={(e) => { e.target.style.borderColor = 'var(--border, #cbd5e1)'; e.target.style.background = '#f8fafc'; e.target.style.boxShadow = 'inset 0 1px 2px rgba(0,0,0,0.02)'; }}
                  value={(packages[editingLimitIdx]?.limits?.maxDeployments ?? packages[editingLimitIdx]?.maxDeployments) ?? ''}
                  onChange={(e) => {
                    const newPackages = [...packages];
                    if (!newPackages[editingLimitIdx].limits) newPackages[editingLimitIdx].limits = {};
                    newPackages[editingLimitIdx].limits.maxDeployments = e.target.value === '' ? '' : parseInt(e.target.value, 10);
                    setPackages(newPackages);
                  }}
                  placeholder="e.g. 5"
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted, #64748b)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.5rem' }}>Max Storage (GB)</label>
                <input
                  type="number"
                  style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border, #cbd5e1)', borderRadius: '8px', fontSize: '0.95rem', background: '#f8fafc', color: 'var(--text-main, #0f172a)', transition: 'all 0.2s', outline: 'none', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)' }}
                  onFocus={(e) => { e.target.style.borderColor = 'var(--primary, #3b82f6)'; e.target.style.background = '#fff'; e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)'; }}
                  onBlur={(e) => { e.target.style.borderColor = 'var(--border, #cbd5e1)'; e.target.style.background = '#f8fafc'; e.target.style.boxShadow = 'inset 0 1px 2px rgba(0,0,0,0.02)'; }}
                  value={(packages[editingLimitIdx]?.limits?.maxStorageGB ?? packages[editingLimitIdx]?.maxStorageGB) ?? ''}
                  onChange={(e) => {
                    const newPackages = [...packages];
                    if (!newPackages[editingLimitIdx].limits) newPackages[editingLimitIdx].limits = {};
                    newPackages[editingLimitIdx].limits.maxStorageGB = e.target.value === '' ? '' : parseInt(e.target.value, 10);
                    setPackages(newPackages);
                  }}
                  placeholder="e.g. 10"
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted, #64748b)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.5rem' }}>Max Total Memory</label>
                <input
                  type="text"
                  style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border, #cbd5e1)', borderRadius: '8px', fontSize: '0.95rem', background: '#f8fafc', color: 'var(--text-main, #0f172a)', transition: 'all 0.2s', outline: 'none', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)' }}
                  onFocus={(e) => { e.target.style.borderColor = 'var(--primary, #3b82f6)'; e.target.style.background = '#fff'; e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)'; }}
                  onBlur={(e) => { e.target.style.borderColor = 'var(--border, #cbd5e1)'; e.target.style.background = '#f8fafc'; e.target.style.boxShadow = 'inset 0 1px 2px rgba(0,0,0,0.02)'; }}
                  value={(packages[editingLimitIdx]?.limits?.maxTotalMemory ?? packages[editingLimitIdx]?.maxTotalMemory) ?? ''}
                  onChange={(e) => {
                    const newPackages = [...packages];
                    if (!newPackages[editingLimitIdx].limits) newPackages[editingLimitIdx].limits = {};
                    newPackages[editingLimitIdx].limits.maxTotalMemory = e.target.value;
                    setPackages(newPackages);
                  }}
                  placeholder="e.g. 10g or 1536m"
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted, #64748b)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.5rem' }}>Memory Per Container</label>
                <input
                  type="text"
                  style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border, #cbd5e1)', borderRadius: '8px', fontSize: '0.95rem', background: '#f8fafc', color: 'var(--text-main, #0f172a)', transition: 'all 0.2s', outline: 'none', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)' }}
                  onFocus={(e) => { e.target.style.borderColor = 'var(--primary, #3b82f6)'; e.target.style.background = '#fff'; e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)'; }}
                  onBlur={(e) => { e.target.style.borderColor = 'var(--border, #cbd5e1)'; e.target.style.background = '#f8fafc'; e.target.style.boxShadow = 'inset 0 1px 2px rgba(0,0,0,0.02)'; }}
                  value={(packages[editingLimitIdx]?.limits?.maxMemoryPerContainer ?? packages[editingLimitIdx]?.maxMemoryPerContainer) ?? ''}
                  onChange={(e) => {
                    const newPackages = [...packages];
                    if (!newPackages[editingLimitIdx].limits) newPackages[editingLimitIdx].limits = {};
                    newPackages[editingLimitIdx].limits.maxMemoryPerContainer = e.target.value;
                    setPackages(newPackages);
                  }}
                  placeholder="e.g. 2g or 512m"
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted, #64748b)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.5rem' }}>Max Total CPU Cores</label>
                <input
                  type="number"
                  step="0.1"
                  style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border, #cbd5e1)', borderRadius: '8px', fontSize: '0.95rem', background: '#f8fafc', color: 'var(--text-main, #0f172a)', transition: 'all 0.2s', outline: 'none', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)' }}
                  onFocus={(e) => { e.target.style.borderColor = 'var(--primary, #3b82f6)'; e.target.style.background = '#fff'; e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)'; }}
                  onBlur={(e) => { e.target.style.borderColor = 'var(--border, #cbd5e1)'; e.target.style.background = '#f8fafc'; e.target.style.boxShadow = 'inset 0 1px 2px rgba(0,0,0,0.02)'; }}
                  value={(packages[editingLimitIdx]?.limits?.maxTotalCpu ?? packages[editingLimitIdx]?.maxTotalCpu) ?? ''}
                  onChange={(e) => {
                    const newPackages = [...packages];
                    if (!newPackages[editingLimitIdx].limits) newPackages[editingLimitIdx].limits = {};
                    newPackages[editingLimitIdx].limits.maxTotalCpu = e.target.value === '' ? '' : parseFloat(e.target.value);
                    setPackages(newPackages);
                  }}
                  placeholder="e.g. 4.0"
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted, #64748b)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.5rem' }}>CPU Per Container</label>
                <input
                  type="number"
                  step="0.1"
                  style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border, #cbd5e1)', borderRadius: '8px', fontSize: '0.95rem', background: '#f8fafc', color: 'var(--text-main, #0f172a)', transition: 'all 0.2s', outline: 'none', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)' }}
                  onFocus={(e) => { e.target.style.borderColor = 'var(--primary, #3b82f6)'; e.target.style.background = '#fff'; e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)'; }}
                  onBlur={(e) => { e.target.style.borderColor = 'var(--border, #cbd5e1)'; e.target.style.background = '#f8fafc'; e.target.style.boxShadow = 'inset 0 1px 2px rgba(0,0,0,0.02)'; }}
                  value={(packages[editingLimitIdx]?.limits?.maxCpuPerContainer ?? packages[editingLimitIdx]?.maxCpuPerContainer) ?? ''}
                  onChange={(e) => {
                    const newPackages = [...packages];
                    if (!newPackages[editingLimitIdx].limits) newPackages[editingLimitIdx].limits = {};
                    newPackages[editingLimitIdx].limits.maxCpuPerContainer = e.target.value === '' ? '' : parseFloat(e.target.value);
                    setPackages(newPackages);
                  }}
                  placeholder="e.g. 1.0"
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted, #64748b)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.5rem' }}>Max Team Members</label>
                <input
                  type="number"
                  style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border, #cbd5e1)', borderRadius: '8px', fontSize: '0.95rem', background: '#f8fafc', color: 'var(--text-main, #0f172a)', transition: 'all 0.2s', outline: 'none', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)' }}
                  onFocus={(e) => { e.target.style.borderColor = 'var(--primary, #3b82f6)'; e.target.style.background = '#fff'; e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)'; }}
                  onBlur={(e) => { e.target.style.borderColor = 'var(--border, #cbd5e1)'; e.target.style.background = '#f8fafc'; e.target.style.boxShadow = 'inset 0 1px 2px rgba(0,0,0,0.02)'; }}
                  value={(packages[editingLimitIdx]?.limits?.maxTeamMembers ?? packages[editingLimitIdx]?.maxTeamMembers) ?? ''}
                  onChange={(e) => {
                    const newPackages = [...packages];
                    if (!newPackages[editingLimitIdx].limits) newPackages[editingLimitIdx].limits = {};
                    newPackages[editingLimitIdx].limits.maxTeamMembers = e.target.value === '' ? '' : parseInt(e.target.value, 10);
                    setPackages(newPackages);
                  }}
                  placeholder="e.g. 5"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{ padding: '1.25rem 2rem', borderTop: '1px solid var(--border-subtle, #f1f5f9)', display: 'flex', justifyContent: 'flex-end', background: 'var(--bg-subtle, #f8fafc)', borderBottomLeftRadius: '16px', borderBottomRightRadius: '16px' }}>
              <button
                style={{ background: 'var(--primary, #0f172a)', color: 'white', border: 'none', padding: '0.65rem 1.75rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                onClick={() => setEditingLimitIdx(null)}
                onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'; }}
                onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'; }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingPackagesSection;
